import React, { useState } from 'react';
import api from '../../api';
import Navbar from '../../components/Navbar';
import Footer from '../../components/Footer';

const CandleGapFillerPage = () => {
  const [selectedDate, setSelectedDate] = useState(
    new Date().toISOString().split('T')[0] // Today's date
  );
  const [loading, setLoading] = useState(false);
  const [filling, setFilling] = useState(false);
  const [gapResult, setGapResult] = useState(null);
  const [fillResult, setFillResult] = useState(null);
  const [error, setError] = useState(null);

  // Check if selected date is today
  const isToday = selectedDate === new Date().toISOString().split('T')[0];

  // Detect gaps for selected date
  const detectGaps = async () => {
    setLoading(true);
    setError(null);
    setGapResult(null);
    setFillResult(null);

    try {
      const response = await api.get(`/api/v1/nifty/detect-candle-gaps?date=${selectedDate}`);
      setGapResult(response.data);
    } catch (err) {
      console.error('Error detecting gaps:', err);
      setError(err.response?.data?.detail || 'Failed to detect gaps');
    } finally {
      setLoading(false);
    }
  };

  // Auto-fill gaps for selected date
  const fillGaps = async () => {
    setFilling(true);
    setError(null);
    setFillResult(null);

    try {
      const response = await api.post(`/api/v1/nifty/auto-fill-gaps?date=${selectedDate}`);
      setFillResult(response.data);
      // Refresh gap detection after filling
      await detectGaps();
    } catch (err) {
      console.error('Error filling gaps:', err);
      setError(err.response?.data?.detail || 'Failed to fill gaps');
    } finally {
      setFilling(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col bg-gray-50">
      <Navbar />
      
      <div className="flex-1 container mx-auto px-4 py-8 max-w-4xl">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900">Candle Gap Filler</h1>
          <p className="mt-2 text-gray-600">
            Detect and fill missing 1-minute candle data in TimescaleDB
          </p>
        </div>

        {/* Warning for Today's Date */}
        {isToday && (
          <div className="bg-orange-50 border border-orange-200 rounded-lg p-4 mb-4">
            <div className="flex items-start gap-2">
              <svg className="h-5 w-5 text-orange-600 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
              </svg>
              <div>
                <p className="text-sm font-semibold text-orange-800 mb-1">
                  ⚠️ Zerodha API Limitation for Today's Data
                </p>
                <p className="text-sm text-orange-700">
                  Zerodha's historical data API does <strong>not provide same-day intraday data</strong> after market hours. 
                  If you're trying to fill gaps for today ({selectedDate}), the data will only be available starting from <strong>tomorrow</strong>.
                </p>
                <p className="text-sm text-orange-700 mt-1">
                  <strong>Recommendation:</strong> Try filling gaps for dates that are at least 1 day old for best results.
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Date Selection */}
        <div className="bg-white rounded-lg shadow p-6 mb-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Select Date</h2>
          
          <div className="flex flex-wrap gap-4 items-end">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Date
              </label>
              <input
                type="date"
                value={selectedDate}
                onChange={(e) => setSelectedDate(e.target.value)}
                className="px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
            
            <button
              onClick={detectGaps}
              disabled={loading || !selectedDate}
              className="px-6 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {loading ? (
                <span className="flex items-center gap-2">
                  <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                  </svg>
                  Detecting...
                </span>
              ) : (
                'Detect Gaps'
              )}
            </button>

            {gapResult && gapResult.gaps_found > 0 && (
              <button
                onClick={fillGaps}
                disabled={filling}
                className="px-6 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {filling ? (
                  <span className="flex items-center gap-2">
                    <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                    </svg>
                    Filling Gaps...
                  </span>
                ) : (
                  `Fill ${gapResult.gaps_found} Gap(s)`
                )}
              </button>
            )}
          </div>
        </div>

        {/* Error Message */}
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
            <div className="flex items-center gap-2 text-red-800">
              <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
              </svg>
              <span className="font-medium">Error:</span>
              <span>{error}</span>
            </div>
          </div>
        )}

        {/* Fill Result */}
        {fillResult && (
          <div className={`rounded-lg p-4 mb-6 border ${
            fillResult.gaps_filled === fillResult.gaps_detected && fillResult.candles_saved > 0
              ? 'bg-green-50 border-green-200'
              : fillResult.gaps_filled > 0
              ? 'bg-yellow-50 border-yellow-200'
              : 'bg-red-50 border-red-200'
          }`}>
            <div className={`flex items-center gap-2 mb-3 ${
              fillResult.gaps_filled === fillResult.gaps_detected && fillResult.candles_saved > 0
                ? 'text-green-800'
                : fillResult.gaps_filled > 0
                ? 'text-yellow-800'
                : 'text-red-800'
            }`}>
              {fillResult.gaps_filled === fillResult.gaps_detected && fillResult.candles_saved > 0 ? (
                <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                </svg>
              ) : fillResult.gaps_filled > 0 ? (
                <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                </svg>
              ) : (
                <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                </svg>
              )}
              <span className="font-semibold">{fillResult.message}</span>
            </div>
            
            <div className={`text-sm space-y-1 ${
              fillResult.gaps_filled === fillResult.gaps_detected && fillResult.candles_saved > 0
                ? 'text-green-700'
                : fillResult.gaps_filled > 0
                ? 'text-yellow-700'
                : 'text-red-700'
            }`}>
              <p><strong>Gaps Detected:</strong> {fillResult.gaps_detected}</p>
              <p><strong>Gaps Filled:</strong> {fillResult.gaps_filled}</p>
              <p><strong>Total Candles Saved:</strong> {fillResult.candles_saved}</p>
            </div>

            {/* Detailed Gap Results */}
            {fillResult.filled_gaps_detail && fillResult.filled_gaps_detail.length > 0 && (
              <div className="mt-4 pt-4 border-t border-gray-300">
                <h4 className="font-semibold text-gray-900 mb-2">Gap Fill Details:</h4>
                <div className="space-y-2">
                  {fillResult.filled_gaps_detail.map((gap, index) => (
                    <div key={index} className={`p-3 rounded ${
                      gap.status === 'complete' ? 'bg-green-100' :
                      gap.status === 'partial' ? 'bg-yellow-100' :
                      'bg-red-100'
                    }`}>
                      <div className="flex items-center justify-between">
                        <span className="font-medium text-gray-900">
                          Gap {index + 1}: {gap.start_ist} - {gap.end_ist}
                        </span>
                        <span className={`px-2 py-1 rounded text-xs font-medium ${
                          gap.status === 'complete' ? 'bg-green-200 text-green-800' :
                          gap.status === 'partial' ? 'bg-yellow-200 text-yellow-800' :
                          'bg-red-200 text-red-800'
                        }`}>
                          {gap.status === 'complete' ? '✓ Complete' :
                           gap.status === 'partial' ? '⚠ Partial' :
                           '✗ Failed'}
                        </span>
                      </div>
                      <div className="mt-1 text-sm text-gray-700">
                        <p>Expected: {gap.expected_candles} candles | 
                           Fetched: {gap.candles_fetched} candles | 
                           Saved: {gap.candles_saved} candles</p>
                        {gap.success_rate !== undefined && (
                          <p className="mt-1">
                            Success Rate: <strong>{gap.success_rate}%</strong>
                            {gap.success_rate < 100 && gap.success_rate > 0 && (
                              <span className="ml-2 text-xs text-yellow-700">
                                (Zerodha API may have limited data availability)
                              </span>
                            )}
                          </p>
                        )}
                        {gap.error && (
                          <p className="mt-1 text-red-600 text-xs">Error: {gap.error}</p>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Gap Detection Results */}
        {gapResult && (
          <div className="bg-white rounded-lg shadow p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">
              Gap Detection Results for {gapResult.date}
            </h2>

            {/* Summary Cards */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
              <div className="bg-blue-50 rounded-lg p-4">
                <p className="text-sm text-blue-600 font-medium">Expected Candles</p>
                <p className="text-2xl font-bold text-blue-900">{gapResult.total_expected_candles}</p>
              </div>
              <div className="bg-green-50 rounded-lg p-4">
                <p className="text-sm text-green-600 font-medium">Existing Candles</p>
                <p className="text-2xl font-bold text-green-900">{gapResult.total_existing_candles}</p>
              </div>
              <div className={`rounded-lg p-4 ${gapResult.total_missing_candles > 0 ? 'bg-red-50' : 'bg-green-50'}`}>
                <p className={`text-sm font-medium ${gapResult.total_missing_candles > 0 ? 'text-red-600' : 'text-green-600'}`}>
                  Missing Candles
                </p>
                <p className={`text-2xl font-bold ${gapResult.total_missing_candles > 0 ? 'text-red-900' : 'text-green-900'}`}>
                  {gapResult.total_missing_candles}
                </p>
              </div>
              <div className={`rounded-lg p-4 ${gapResult.gaps_found > 0 ? 'bg-yellow-50' : 'bg-green-50'}`}>
                <p className={`text-sm font-medium ${gapResult.gaps_found > 0 ? 'text-yellow-600' : 'text-green-600'}`}>
                  Gaps Found
                </p>
                <p className={`text-2xl font-bold ${gapResult.gaps_found > 0 ? 'text-yellow-900' : 'text-green-900'}`}>
                  {gapResult.gaps_found}
                </p>
              </div>
            </div>

            {/* Market Hours Info */}
            <div className="bg-gray-50 rounded-lg p-4 mb-6">
              <p className="text-sm text-gray-600">
                <span className="font-medium">Market Hours (IST):</span>{' '}
                {gapResult.market_hours?.open_ist} - {gapResult.market_hours?.close_ist}
              </p>
            </div>

            {/* Gap Details Table */}
            {gapResult.gaps && gapResult.gaps.length > 0 ? (
              <div>
                <h3 className="text-md font-semibold text-gray-800 mb-3">Gap Details</h3>
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">#</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Start Time (IST)</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">End Time (IST)</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Missing Minutes</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Duration</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {gapResult.gaps.map((gap, index) => (
                        <tr key={index} className="hover:bg-gray-50">
                          <td className="px-4 py-3 text-sm text-gray-900">{index + 1}</td>
                          <td className="px-4 py-3 text-sm text-gray-900 font-medium">{gap.start_ist}</td>
                          <td className="px-4 py-3 text-sm text-gray-900 font-medium">{gap.end_ist}</td>
                          <td className="px-4 py-3 text-sm">
                            <span className="px-2 py-1 bg-red-100 text-red-800 rounded-full text-xs font-medium">
                              {gap.missing_minutes} min
                            </span>
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-600">
                            {gap.missing_minutes >= 60 
                              ? `${Math.floor(gap.missing_minutes / 60)}h ${gap.missing_minutes % 60}m`
                              : `${gap.missing_minutes}m`
                            }
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            ) : (
              <div className="text-center py-8">
                <svg className="mx-auto h-12 w-12 text-green-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <p className="mt-2 text-lg font-medium text-green-800">No Gaps Found!</p>
                <p className="text-sm text-gray-600">All candle data is complete for this date.</p>
              </div>
            )}
          </div>
        )}

        {/* Instructions */}
        {!gapResult && !loading && (
          <div className="bg-white rounded-lg shadow p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">How to Use</h2>
            <ol className="list-decimal list-inside space-y-2 text-gray-700">
              <li>Select a date from the date picker above</li>
              <li>Click <strong>"Detect Gaps"</strong> to scan the database for missing candle data</li>
              <li>If gaps are found, click <strong>"Fill Gaps"</strong> to fetch data from Zerodha and save to database</li>
              <li>The system will automatically fetch missing 1-minute candles for market hours (9:15 AM - 3:30 PM IST)</li>
            </ol>
            
            <div className="mt-4 p-4 bg-yellow-50 rounded-lg border border-yellow-200">
              <div className="flex items-start gap-2">
                <svg className="h-5 w-5 text-yellow-600 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                </svg>
                <div>
                  <p className="text-sm font-medium text-yellow-800 mb-1">
                    <strong>Important Zerodha API Limitation:</strong>
                  </p>
                  <p className="text-sm text-yellow-700">
                    Zerodha's historical data API does <strong>not provide same-day intraday data</strong> after market hours. 
                    If you're trying to fill gaps for today's date, the data will only be available starting from the <strong>next trading day</strong>.
                  </p>
                  <p className="text-sm text-yellow-700 mt-2">
                    <strong>Recommendation:</strong> Fill gaps for dates that are at least 1 day old for best results.
                  </p>
                </div>
              </div>
            </div>
            
            <div className="mt-4 p-4 bg-blue-50 rounded-lg">
              <p className="text-sm text-blue-800">
                <strong>Note:</strong> Make sure you have an active Zerodha session to fetch data. 
                The system uses your broker credentials to retrieve historical candle data.
              </p>
            </div>
          </div>
        )}
      </div>

      <Footer />
    </div>
  );
};

export default CandleGapFillerPage;
